// @ts-check
/** <custom-footer-nav> wraps each footer column: exclusive accordion under 1024px, always expanded
 * on desktop. State is the `open` attribute; CSS derives panel height/visibility/icon from it. */

import { Component } from '@theme/component';
import { waitForChatWidget } from '@theme/custom.chat-widget';

/** @typedef {import('@theme/custom.chat-widget').ChatWidget} ChatWidget */

/** Desktop breakpoint - mirrors the CSS 1024px switch in custom-footer.liquid. */
const DESKTOP = window.matchMedia('(min-width: 1024px)');

/**
 * @extends {Component<{toggle: HTMLButtonElement, title?: HTMLElement}>}
 */
class CustomFooterNav extends Component {
  requiredRefs = ['toggle'];

  /** @type {boolean} */
  #open = false;

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    DESKTOP.addEventListener('change', this.#handleBreakpoint, { signal });

    if (window.Shopify?.designMode) {
      // Bubbles from the child blocks and fires for the column itself; the editor re-fires it
      // after a section re-render, so a selected column reopens without a section:load hook.
      this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** Toggle handler for the column heading button (delegated via `on:click`). */
  toggleColumn = () => {
    if (DESKTOP.matches) return;
    if (this.#open) {
      this.#close();
    } else {
      this.#openExclusive();
    }
  };

  #handleBlockSelect = () => {
    if (DESKTOP.matches || this.#open) return;
    this.#openExclusive();
  };

  #handleBreakpoint = () => {
    // Reset open state when crossing the breakpoint so mobile state
    // doesn't bleed into a resize-back-to-mobile scenario.
    if (!DESKTOP.matches) this.#open = false;
    this.#sync();
  };

  #openExclusive() {
    const root = this.closest('.custom-footer') ?? document;
    for (const nav of root.querySelectorAll('custom-footer-nav')) {
      if (nav !== this && nav instanceof CustomFooterNav) nav.#close();
    }
    this.#open = true;
    this.#sync();
  }

  #close() {
    if (!this.#open) return;
    this.#open = false;
    this.#sync();
  }

  #sync() {
    const expanded = DESKTOP.matches || this.#open;
    this.toggleAttribute('open', expanded);
    const { toggle, title } = this.refs;
    // On desktop the button toggles nothing, so it is hidden and the heading names the column.
    if (DESKTOP.matches) {
      toggle.removeAttribute('aria-expanded');
      toggle.setAttribute('tabindex', '-1');
      toggle.setAttribute('aria-hidden', 'true');
      title?.removeAttribute('hidden');
    } else {
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.removeAttribute('tabindex');
      toggle.removeAttribute('aria-hidden');
      title?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('custom-footer-nav')) {
  customElements.define('custom-footer-nav', CustomFooterNav);
}

/** <custom-chat-cta> opens the store's chat widget from our own footer pill. It ships hidden
 * because the widget comes from an app embed one store has and another does not.
 *
 * The pill replaces the widget's floating launcher, so a store with a chat app either adds this
 * pill or keeps the bubble, never both and never neither.
 *
 * @extends {Component<{trigger: HTMLButtonElement}>}
 */
class CustomChatCta extends Component {
  requiredRefs = ['trigger'];

  /** @type {ChatWidget | null} */
  #widget = null;

  connectedCallback() {
    super.connectedCallback();
    // The editor has to show the block whatever the storefront has installed, or a merchant
    // cannot select it to change its settings. There it leaves the widget's own bubble alone.
    if (window.Shopify?.designMode) this.hidden = false;
    // The HOST carries `hidden`, not the button: the footer column is a flex column with a gap, so
    // a revealed-later button inside a visible wrapper would still hold its 12px of it open.
    void this.#connectWidget();
  }

  async #connectWidget() {
    const widget = await waitForChatWidget(() => this.isConnected);
    if (!widget) return;
    if (!window.Shopify?.designMode) widget.ownLauncher();
    this.#widget = widget;
    this.hidden = false;
  }

  /** Click handler for the pill (delegated via `on:click`). */
  openChat = () => {
    this.#widget?.open();
  };
}

if (!customElements.get('custom-chat-cta')) {
  customElements.define('custom-chat-cta', CustomChatCta);
}
